import config from '@/config';
import { useAuthStore } from '@/stores/auth';
import { pinia } from '@/stores/pinia';

let Connection: WebSocket | undefined;
let shouldReconnect = true;

const getNotificationWebsocketEndpoint = (id: string): string | null => {
	if (config.isLocalGateway) {
		return `ws://localhost:50090/api/v1/ws/user/${id}`;
	}

	if (!config.baseDomain) {
		return null;
	}

	const gatewayUrl = config.baseDomain.includes('://')
		? config.baseDomain
		: `https://${config.baseDomain}`;
	const gatewayHost = new URL(gatewayUrl).host;

	return `wss://${gatewayHost}/api/v1/ws/user/${id}`;
};

// This function checks if the Connection is open (where readyState === 1 means OPEN). If it is open it setting the flag to false and it closes the WebSocket connection.
export const KillWebsocket = (): void => {
	Connection && Connection.readyState === WebSocket.OPEN && Connection.close();
};

// This simply returns the current WebSocket Connection.
export const GetNotificationWebsocket = (): WebSocket | undefined => {
	return Connection;
};

// Waits for 15 seconds.
// Saves the current onmessage event handler for the Connection.
// Starts a new WebSocket connection.
// Restores the onmessage event handler to the newly established Connection.
export const ReOpenWebsocket = async (): Promise<void> => {
	await new Promise((resolve) => setTimeout(resolve, 15000));
	const onMessage = Connection?.onmessage ?? null;

	StartNotificationWebsocket();

	if (Connection) {
		Connection.onmessage = onMessage;
	}
};

// First, it checks if a Connection already exists and is neither closing nor closed. If true, it simply returns the existing Connection.
// Constructs the wsEndpoint based on configurations and the passed user id. If it's running on a local gateway, the endpoint is set to a local address; otherwise, it creates an endpoint from the domain in the configuration.
// Initiates a new WebSocket connection with the constructed endpoint.
// Defines event handlers for the WebSocket:
// onopen: Sends a "PING" message once the WebSocket is opened.
// onerror: Logs errors if the environment is not "local".
// onclose: Logs the close message if not in a local environment and attempts to reopen the WebSocket if the flag is true
// Assigns the newly created WebSocket instance to Connection.
// Returns the newly created WebSocket.
export const StartNotificationWebsocket = (
	_id?: string
): WebSocket | undefined => {
	const authStore = useAuthStore(pinia);
	const id = localStorage.getItem('session_id');
	if (
		Connection &&
		Connection.readyState !== WebSocket.CLOSED &&
		Connection.readyState !== WebSocket.CLOSING
	) {
		return Connection;
	}

	if (
		id === null ||
		id === undefined ||
		!authStore.isAuthenticated() ||
		authStore.isSessionExpired()
	) {
		return; // we should not start the websocket if the id is null or its not authenticated
	}

	const wsEndpoint = getNotificationWebsocketEndpoint(id);
	if (!wsEndpoint) {
		return;
	}
	const ws = new WebSocket(wsEndpoint);

	ws.onopen = () => {
		ws.send('PING');
	};

	ws.onerror = (err) => config.environment !== 'local' && console.log(err);

	ws.onclose = (closeMessage) => {
		config.environment !== 'local' && console.log(closeMessage);

		if (shouldReconnect) {
			ReOpenWebsocket();
		}
	};

	Connection = ws;
	return ws;
};
